import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getRoot } from 'mobx-easy';
import { useTranslation } from 'react-i18next';
import { Modal, Spinner } from '@bringg/react-components';
import notification from '@bringg/react-components/dist/components/notification/notification';
import { UserOptimizationData } from '@bringg/dashboard-sdk/dist/RouteOptimization/RouteOptimization.consts';
import {
	Team,
	User,
	UserType,
	DeliveryBlock,
	OPTIMIZATION_TYPE,
	ParkingSpot,
	CustomerAvailabilityHour,
	CalculateRouteKpiRequest
} from '@bringg/types';
import {
	ConsecutiveRoutes,
	NO_ROUTE_ID,
	OpenUserAssignerProps,
	RoutesPlannerRun,
	RoutesPlannerTask,
	UpdateVehicleOnRunProps,
	USE_OWN_FLEET_OPTIMIZATION_SERVICE_ENDPOINTS
} from '@bringg-frontend/routes-planner';
import { getCustomerIdByTaskInventory } from '@bringg-frontend/shared-tasks-weight';

import useServiceAreas from 'bringg-web/hooks/use-service-areas';
import { useStores } from 'bringg-web/recipes';
import RoutesPlannerManager from 'bringg-web/features/routes-planner/routes-planner-manager';
import { tasksById } from 'bringg-web/utils/tasks-utils';
import { getAllUserTypes } from 'bringg-web/utils/user-types-utils';
import useVehicleType from 'bringg-web/hooks/use-vehicle-type';
import { OptimizationModalProps } from './routes-planner-modal-types';
import {
	getTaskUsersType,
	updateEtaResponse,
	getRouteInfoByIndex,
	getRunIdByRouteIndex
} from './routes-planner-data-creator/routes-planner-data-creator';
import useCreateRoutesPlannedModalData from './use-create-routes-planned-modal-data';
import { getTeamDeliveryBlocks } from '../../utils/delivery-blocks-utils';
import { USER_BASED_OPTIMIZATION_TYPES } from '../../features/routes-planner/models';
import { Dictionary } from '../../types/dictionary';
import { useHasFeatureFlag } from '../../utils/feature-flags';
import { consecutiveRoutesLoader } from '../../services/consecutive-routes/consecutive-routes-loader';
import RootStore from '../../stores/root-store';
import * as parkingSpotsProvider from '../../services/parking-spots-provider';
import { routesPlannerTasksConverter } from './services/routes-planner-tasks-converter/routes-planner-tasks-converter';
import { routesPlannerRunConverter } from './services/routes-planner-run-converter/routes-planner-run-converter';
import { ROUTE_OPTIMIZATION_FUNCTIONALITY, VALID_TICKET_TYPES } from 'bringg-web/features/submit-ticket/consts';
import { timezoneProvider } from 'bringg-web/services/timezone/timezone-provider';
import { executeAction } from 'bringg-web/services/cross-application/cross-application';
import { ModalAction } from 'bringg-web/services/cross-application/cross-application.actions';
import { SERVICE_AREA_LAT_LNG_FF } from 'bringg-web/consts';
import { userAssignerModalOpener } from './services/user-assigner-modal-opener/user-assigner-modal-opener';
import UserAssignerModal, {
	UserAssignerModalProps
} from 'bringg-web/features/runs-assignment/user-assignment/user-assigner-modal';
import { CloseDataType } from 'bringg-web/features/runs-assignment/user-assignment/consts';
import VehicleAssignerModal, {
	VehicleAssignerModalProps
} from 'bringg-web/features/runs-assignment/vehicle-assignment/vehicle-assigner-modal';
import { vehicleAssignerModalOpener } from './services/vehicle-assigner-modal-opener/vehicle-assigner-modal-opener';
import { RouteOptimizerConfigService } from 'bringg-web/features/routes-planner/route-optimizer-config-service';
import { configurationService } from 'bringg-web/features/routes-planner/configuration-service';

import './routes-planner-modal.scss';

const RoutesPlannerModal = ({ onClose, modalParams, isReadOnly = false }: OptimizationModalProps) => {
	const {
		optimizationResults,
		isOpenFromDispatch,
		teamId,
		selectedTasks,
		optimizationUuid,
		isManualOptimization,
		optimizationType,
		onApplyChanges,
		disableAssignDriver
	} = modalParams;
	const { t } = useTranslation();
	const {
		authStore,
		usersStore,
		teamsStore,
		merchantConfigurationsStore,
		runStore,
		tasksStore,
		vehiclesStore,
		vehicleTypesStore,
		customAttributesStore,
		applicationTeamConfigurationStore,
		applicationMerchantConfigurationStore,
		teamConfiguration: teamConfigurationApi
	} = useStores();
	const { currentUser } = usersStore;
	const { configuration } = merchantConfigurationsStore;
	const { custom_units_definition } = configuration;

	const { getAllServiceAreas } = useServiceAreas(
		teamsStore,
		teamId,
		true,
		useHasFeatureFlag(SERVICE_AREA_LAT_LNG_FF)
	);
	const { isRouteAssignAllowed } = useCreateRoutesPlannedModalData();
	const { getVehicleTypeByVehicleIds } = useVehicleType();
	const consecutiveRoutesFF = useHasFeatureFlag('consecutive_routes');

	const [selectedTasksObject, setSelectedTasksObject] = useState<{ [key: string]: RoutesPlannerTask }>({});
	const [selectedTeam, setSelectedTeam] = useState<Team>(null);
	const [deliveryBlocks, setDeliveryBlocks] = useState<DeliveryBlock[]>(null);
	const [drivers, setDrivers] = useState<{ [key: string]: User }>({});
	const [breaks, setBreaks] = useState({});
	const [parkingSpots, setTeamParkingSpots] = useState<ParkingSpot[]>([]);
	const [directions, setDirections] = useState([]);
	const [updatedRoutesPlannerConfig, setUpdatedRoutesPlannerConfig] = useState(
		configurationService.getRoutesPlannerConfiguration(merchantConfigurationsStore.configuration)
	);
	const [userTypes, setUserTypes] = useState<UserType[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isConfigLoading, setIsConfigLoading] = useState(true);
	const [routes, setRoutes] = useState<{ [runId: number]: RoutesPlannerRun }>({});
	const [routesPlannerTasks, setRoutesPlannerTasks] = useState<RoutesPlannerTask[]>([]);
	const [consecutiveRoutes, setConsecutiveRoutes] = useState<ConsecutiveRoutes>({
		routesById: new Map<number, RoutesPlannerRun>(),
		tasksById: new Map<number, RoutesPlannerTask>()
	});
	const [customerAvailabilityHours, setAvailabilityHours] = useState<{
		[customerId: string]: CustomerAvailabilityHour[];
	}>({});
	const [userAssignerModalProps, setUserAssignerModalProps] = useState<UserAssignerModalProps>();
	const [vehicleAssignerModalProps, setVehicleAssignerModalProps] = useState<VehicleAssignerModalProps>();

	const useOwnFleetOptimizationServiceEndpoints = useHasFeatureFlag(USE_OWN_FLEET_OPTIMIZATION_SERVICE_ENDPOINTS);
	const useInitialLoadingTime = useHasFeatureFlag('use_initial_loading_time_in_delivery_blocks');

	useEffect(() => {
		fetchConfig();
		fetchData();
	}, []);

	const optimizationData = optimizationResults?.data ? [...optimizationResults.data] : [];
	const customerIdByTaskInventory = useMemo(() => getCustomerIdByTaskInventory(selectedTasks), [selectedTasks]);

	const vehicleTypeByVehicleIds = getVehicleTypeByVehicleIds();

	const getDriversData = async () => {
		const driversById = {};
		const drivers = await getRoot<RootStore>().data.driversStore.fetchByTeam(teamId);

		drivers.forEach(driver => {
			driversById[driver.id] = driver;
		});

		const teamDeliveryBlocks = await getTeamDeliveryBlocks(selectedTasks, teamId);
		setDeliveryBlocks(teamDeliveryBlocks);
		setDrivers(driversById);
	};

	const createRoutes = (tasks: RoutesPlannerTask[]): { [runId: number]: RoutesPlannerRun } => {
		const plannedRoutesObject = {};
		tasks.forEach(task => {
			const runId = task.run_id;

			if (runId !== NO_ROUTE_ID) {
				if (!plannedRoutesObject[runId]) {
					const run = runStore.get(runId);

					plannedRoutesObject[runId] = routesPlannerRunConverter.convert(
						runId,
						run,
						optimizationData,
						task,
						isManualOptimization,
						t
					);
				}
			}
		});
		return plannedRoutesObject;
	};

	const updateTasksToRoutesPlanner = () => {
		const tasksObject = {};
		selectedTasks.forEach(task => {
			tasksObject[task.id] = routesPlannerTasksConverter.convert(task);
		});
		setSelectedTasksObject(tasksObject);
		return tasksObject;
	};

	const updateOptimizationData = () => {
		let updateSelectedTasksObject: { [key: string]: RoutesPlannerTask } = {};
		let optimizationBreaks = {};
		let optimizationDirections = [];

		const runIdByRouteIndex = getRunIdByRouteIndex(optimizationData);
		const tasksToObject = tasksById(selectedTasks);

		optimizationData.forEach(result => {
			const taskId = result.task_id || result.id;
			const selectedTask = tasksToObject[taskId];

			const { updateDirections, updateBreaks, updateSelectedTasks } = updateEtaResponse({
				selectedTask,
				runIdByRouteIndex,
				optimizationResult: result,
				selectedTasksObject,
				breaks: optimizationBreaks
			});

			updateSelectedTasksObject = { ...updateSelectedTasksObject, ...updateSelectedTasks };
			optimizationBreaks = { ...optimizationBreaks, ...updateBreaks };
			optimizationDirections = [...optimizationDirections, ...updateDirections];
		});

		if (isManualOptimization) {
			Object.values(updateSelectedTasksObject).forEach(task => {
				task.run_id = task.run_id || NO_ROUTE_ID;
			});
		}

		setSelectedTasksObject(updateSelectedTasksObject);
		setBreaks(optimizationBreaks);
		setDirections(optimizationDirections);
		return updateSelectedTasksObject;
	};

	const getTasksAvailabilityHours = async (tasks: RoutesPlannerTask[]) => {
		const tasksByCustomerId = {};

		tasks.forEach(task => {
			const customerId = task.way_points[0].customer_id;
			if (customerId) {
				if (tasksByCustomerId[customerId]) {
					tasksByCustomerId[customerId].push(task.id);
				} else {
					tasksByCustomerId[customerId] = [task.id];
				}
			}
		});

		try {
			const results = {};
			const availabilityByCustomer =
				await getRootEnv().dashboardSdk.sdk.customerAvailabilityHours.getBatchTaskCustomerAvailabilityHours({
					customers: tasksByCustomerId
				});

			Object.entries(availabilityByCustomer).forEach(([customerId, availabilityHoursByTask]) => {
				const availabilityHours = {};
				Object.values(availabilityHoursByTask).forEach(hoursByTask => {
					hoursByTask.forEach(hour => {
						if (!availabilityHours[hour.id]) {
							availabilityHours[hour.id] = hour;
						}
					});
				});
				results[customerId] = Object.values(availabilityHours);
			});

			return results;
		} catch (e) {
			return {};
		}
	};

	const fetchConfig = async () => {
		setIsConfigLoading(true);
		const baseConfig = configurationService.getRoutesPlannerConfiguration(
			merchantConfigurationsStore.configuration
		);
		const config = await RouteOptimizerConfigService.loadRO2Config(
			baseConfig,
			applicationMerchantConfigurationStore,
			applicationTeamConfigurationStore,
			merchantConfigurationsStore,
			teamConfigurationApi,
			teamId,
			useInitialLoadingTime
		);
		setUpdatedRoutesPlannerConfig(config);
		setIsConfigLoading(false);
	};

	const fetchData = async () => {
		try {
			const updatedSelectedTasks: Dictionary<RoutesPlannerTask> = optimizationResults
				? updateOptimizationData()
				: updateTasksToRoutesPlanner();

			const tasksToRoutesPlanner = Object.values(updatedSelectedTasks).sort(
				(task1, task2) => task1.priority - task2.priority
			);

			const createdRoutes = createRoutes(tasksToRoutesPlanner);
			const promises: Promise<any>[] = [
				teamsStore.fetchById(teamId),
				getAllUserTypes(),
				parkingSpotsProvider.getDriverParkingSpots(teamId),
				getTasksAvailabilityHours(Object.values(updatedSelectedTasks)),
				vehiclesStore.loadAll(),
				vehicleTypesStore.fetchAll(),
				getDriversData(),
				customAttributesStore.fetchAll()
			];

			if (consecutiveRoutesFF && isManualOptimization) {
				promises.push(
					consecutiveRoutesLoader
						.load(
							Object.values(createdRoutes),
							runStore,
							tasksStore,
							updatedRoutesPlannerConfig,
							isOpenFromDispatch,
							optimizationType,
							t
						)
						.then(setConsecutiveRoutes)
				);
			}

			const [team, allUserTypes, fetchedParkingSpots, availabilityHours] = await Promise.all(promises);
			setAvailabilityHours(availabilityHours);
			setSelectedTeam(team);
			setUserTypes(allUserTypes);
			setRoutes(createdRoutes);
			setRoutesPlannerTasks(tasksToRoutesPlanner);
			setTeamParkingSpots(fetchedParkingSpots);
		} catch (e) {
			console.error(e);
		} finally {
			setIsLoading(false);
		}
	};

	const depsObject = useMemo(() => {
		return {
			getUserType: (
				run: Partial<RoutesPlannerRun>,
				tasks: RoutesPlannerTask[],
				routesPlannerRuns: RoutesPlannerRun[]
			) => {
				return getTaskUsersType(run, tasks, routesPlannerRuns, userTypes, Object.values(drivers));
			},
			fetchOptimizationKpiData: async (request: CalculateRouteKpiRequest) => {
				return await getRootEnv().dashboardSdk.sdk.optimizationKpi.fetchOptimizationKpiData(request);
			},
			getTimezoneByTeamId: (id: number) => timezoneProvider.getTimezoneByTeamId(id)
		};
	}, [drivers, userTypes]);

	const onApply = async (changes, modifiedTasks, errorHandle) => {
		const userOptimizationData: UserOptimizationData = {
			data: changes,
			modified_tasks: modifiedTasks,
			merchant_id: currentUser.merchant_id,
			team_id: teamId
		};

		try {
			let modifiedData;
			if (optimizationUuid) {
				modifiedData = { ...userOptimizationData, request_uuid: optimizationUuid };
				await getRootEnv().dashboardSdk.sdk.routeOptimizationApi.updateOptimizationModifiedData(
					modifiedData,
					useOwnFleetOptimizationServiceEndpoints
				);
				onApplyChanges?.(modifiedData);
			} else {
				modifiedData = { ...userOptimizationData, user_id: 2 };
				await getRootEnv().dashboardSdk.sdk.routeOptimizationApi.assignUserManualOptimization(modifiedData);
			}
			notification.success(t('PLANNING.ASSIGN_USERS_MANUAL_OPTIMIZATION_SUCCESS'));
			onClose();
		} catch (e) {
			notification.error(t('ROUTES_PLANNER.ASSIGN_USERS_MANUAL_OPTIMIZATION_FAIL'));
			errorHandle();
		}
	};

	const namedType =
		optimizationType === OPTIMIZATION_TYPE.COMBINED ? OPTIMIZATION_TYPE.COMBINED : OPTIMIZATION_TYPE.NAMED_VRP;

	const isUnnamed = !USER_BASED_OPTIMIZATION_TYPES.includes(optimizationType);

	const handleOpenSupportTicketModal = useCallback(() => {
		executeAction(authStore.crossAppTransport, ModalAction.SUBMIT_TICKET_MODAL, {
			ticketFields: {
				title: () => t('ROUTES_PLANNER.SUBMIT_TICKET.TITLE'),
				origin: () => 'dashboard',
				type: () => VALID_TICKET_TYPES.incident,
				description: () =>
					`${t('ROUTES_PLANNER.SUBMIT_TICKET.CHECK_INFO_MESSAGE')} \n${t(
						'ROUTES_PLANNER.SUBMIT_TICKET.OPTIMIZATION_ID'
					)} ${optimizationUuid}`,
				affectedFunctionality: () => ROUTE_OPTIMIZATION_FUNCTIONALITY
			}
		});
	}, [optimizationUuid, t]);

	const onOpenUserAssignerModal = async ({ runId, onClose }: OpenUserAssignerProps) => {
		const route = routes[runId];
		const modalParams = await userAssignerModalOpener.getUserAssignerModalParams({
			runId,
			runTasks: routesPlannerTasks.filter(task => task.run_id === runId),
			drivers: Object.values(drivers),
			isSavedRun: !optimizationUuid,
			teamId: selectedTeam.id,
			configuration: updatedRoutesPlannerConfig,
			vehicleTypeByVehicleIds,
			route: route
		});
		const onModalClose = (data?: CloseDataType) => {
			data && onClose(data.targetRunId, data.driver);
			setUserAssignerModalProps(undefined);
		};
		setUserAssignerModalProps({ onClose: onModalClose, modalParams });
	};

	const openVehicleAssignerModal = (
		runId: number,
		onClose: ({ runId, vehicleId, tasksIds }: UpdateVehicleOnRunProps) => void
	) => {
		const route = routes[runId];
		const runTasks = routesPlannerTasks.filter(task => task.run_id === runId);
		const modalParams = vehicleAssignerModalOpener.geVehicleAssignerModalParams(route, runTasks, teamId, onClose);

		setVehicleAssignerModalProps({ onClose: () => setVehicleAssignerModalProps(undefined), modalParams });
	};

	return (
		<>
			<Modal
				data-test-id="routes-planner-modal"
				visible
				onCancel={onClose}
				className="routes-planner-modal"
				closable={false}
				maskClosable={false}
				width="99%"
				footer={null}
				zIndex={1055}
			>
				{isLoading || isConfigLoading ? (
					<Spinner className="routes-planner-spinner" />
				) : (
					<RoutesPlannerManager
						mapApiKey={authStore.mapboxAccessToken}
						users={drivers}
						tasks={routesPlannerTasks}
						breaks={breaks}
						parkingSpots={parkingSpots}
						routes={routes}
						consecutiveRoutes={consecutiveRoutes}
						routeInfoByIndex={getRouteInfoByIndex(optimizationData)}
						deliveryBlocks={deliveryBlocks}
						directions={directions}
						team={selectedTeam}
						optimizationType={!isManualOptimization && isUnnamed ? optimizationType : namedType}
						currentUser={currentUser}
						getAllServiceAreas={getAllServiceAreas}
						isManual={isManualOptimization}
						configuration={updatedRoutesPlannerConfig}
						onCancel={onClose}
						onApply={onApply}
						optimizationRequestId={optimizationUuid}
						vehicleTypeByVehicleIds={vehicleTypeByVehicleIds}
						fromDispatch={isOpenFromDispatch}
						openSupportModal={handleOpenSupportTicketModal}
						deps={depsObject}
						vehicles={vehiclesStore.getAll()}
						openUserAssignerModal={
							isRouteAssignAllowed(routesPlannerTasks, disableAssignDriver)
								? onOpenUserAssignerModal
								: undefined
						}
						openVehicleAssignerModal={openVehicleAssignerModal}
						customerIdByTaskInventory={customerIdByTaskInventory}
						customUnitsDefinitions={custom_units_definition}
						isReadOnly={isReadOnly}
						customerAvailabilityHours={customerAvailabilityHours}
						customAttributes={customAttributesStore.activeCustomAttributes}
					/>
				)}
			</Modal>
			{userAssignerModalProps && <UserAssignerModal {...userAssignerModalProps} />}
			{vehicleAssignerModalProps && <VehicleAssignerModal {...vehicleAssignerModalProps} />}
		</>
	);
};

export default RoutesPlannerModal;
